import React, { useState } from 'react';
import DataTable from '../components/common/DataTable';
import { useToast } from '../hooks/useToast';
import { sendNotification, ensaioCanal } from '../utils/api';
import {
  MessageSquare,
  Send,
  Mail,
  Smartphone,
  Bell,
  Users,
  User,
  Briefcase,
  ShieldCheck
} from 'lucide-react';
import '../styles/MessagesPage.css';

const MessagesPage = () => {
  const [activeTab, setActiveTab] = useState('compose');
  const [notification, setNotification] = useState({
    type: 'email',
    categoria: 'novidades',
    target: 'all',
    subject: '',
    message: '',
    recipientId: '',
  });
  const [sending, setSending] = useState(false);
  const { success, error } = useToast();

  // O servidor pede confirmação antes de mandar email ou SMS a mais do que uma
  // pessoa. Não se confirma por ele: mostra-se quantas são e espera-se um
  // segundo carregar, porque um engano nesta caixa sai para fora e não volta.
  const [porConfirmar, setPorConfirmar] = useState(null);

  const handleSend = async (e, confirmar = false) => {
    e?.preventDefault?.();
    setSending(true);
    try {
      const r = await sendNotification({ ...notification, confirmar });
      success(r?.message || 'Notificação enviada.');
      setPorConfirmar(null);
      setNotification({ type: 'email', categoria: 'novidades', target: 'all', subject: '', message: '', recipientId: '' });
    } catch (err) {
      const d = err.response?.data;
      if (d?.confirmacaoNecessaria) {
        setPorConfirmar(d);
      } else {
        error(d?.message || 'Não foi possível enviar a notificação.');
      }
    } finally {
      setSending(false);
    }
  };

  const tabs = [
    { id: 'compose', label: 'Compor', icon: Send },
    { id: 'history', label: 'Histórico', icon: MessageSquare },
    { id: 'templates', label: 'Templates', icon: Mail },
    { id: 'ensaio', label: 'Ensaio de canais', icon: Smartphone },
  ];

  // Ensaio de canal — "isto chega mesmo?"
  //
  // A pergunta aparece sempre que se mexe nos avisos, e até aqui só havia duas
  // formas de responder: disparar um evento real e esperar que alguém
  // confirmasse, ou trocar o contacto de uma conta para o nosso — mexer em
  // dados de produção para tirar uma dúvida de configuração.
  const [ensaio, setEnsaio] = useState({ canal: 'sms', destino: '', texto: '' });
  const [ensaioBusy, setEnsaioBusy] = useState(false);
  const [ensaioResposta, setEnsaioResposta] = useState(null);

  const enviarEnsaio = async (e) => {
    e.preventDefault();
    setEnsaioBusy(true); setEnsaioResposta(null);
    try {
      const r = await ensaioCanal(ensaio);
      setEnsaioResposta(r);
      if (r.success) success(r.message); else error(r.message);
    } catch (err) {
      const r = err.response?.data || { message: 'Falhou o envio.' };
      setEnsaioResposta(r);
      error(r.message);
    } finally { setEnsaioBusy(false); }
  };

  // O envio de campanhas ainda não guarda histórico na base de dados, por isso
  // não há nada real para listar. Antes mostravam-se 4 campanhas inventadas
  // (1.247 / 386 / 861 destinatários) que nunca existiram.
  const sentHistory = [];

  const historyColumns = [
    { key: 'id', label: 'ID', width: '60px' },
    { key: 'type', label: 'Tipo', render: (row) => (
      <div className="flex items-center gap-1">
        {row.type === 'email' && <Mail className="w-4 h-4 text-blue-400" />}
        {row.type === 'sms' && <Smartphone className="w-4 h-4 text-green-400" />}
        {row.type === 'push' && <Bell className="w-4 h-4 text-purple-400" />}
        <span className="text-sm text-gray-300 capitalize">{row.type}</span>
      </div>
    )},
    { key: 'target', label: 'Destinatário', render: (row) => (
      <span className="text-sm text-gray-300 capitalize">{row.target === 'all' ? 'Todos' : row.target === 'workers' ? 'Profissionais' : row.target === 'clients' ? 'Clientes' : 'Individual'}</span>
    )},
    { key: 'subject', label: 'Assunto' },
    { key: 'recipients', label: 'Destinatários', render: (row) => (
      <span className="text-sm text-gray-300">{row.recipients.toLocaleString('pt-PT')}</span>
    )},
    { key: 'status', label: 'Status', render: (row) => (
      <span className={`px-2 py-1 rounded-full text-xs font-medium
        ${row.status === 'delivered' ? 'bg-green-500/20 text-green-400' :
          row.status === 'failed' ? 'bg-red-500/20 text-red-400' :
          'bg-yellow-500/20 text-yellow-400'}`}>
        {row.status === 'delivered' ? 'Entregue' : row.status === 'failed' ? 'Falhou' : 'Pendente'}
      </span>
    )},
    { key: 'sent_at', label: 'Enviado', render: (row) =>
      new Date(row.sent_at).toLocaleDateString('pt-PT')
    },
  ];

  const templates = [
    { id: 1, name: 'Boas-vindas', type: 'email', description: 'Email de boas-vindas para novos utilizadores' },
    { id: 2, name: 'Aprovação de profissional', type: 'email', description: 'Notificação de aprovação de conta de profissional' },
    { id: 3, name: 'Novo pedido', type: 'sms', description: 'SMS para profissionais sobre novo pedido' },
    { id: 4, name: 'Lembrete de Agendamento', type: 'push', description: 'Notificação push de lembrete' },
    { id: 5, name: 'Mediação aberta', type: 'email', description: 'Email sobre nova mediação' },
  ];

  return (
    <div className="messages-admin-page space-y-6">
      {/* Header */}
      <div className="messages-admin-hero"><div><span>Comunicação operacional</span><h1>Centro de mensagens</h1><p>Comunique atualizações importantes aos clientes e profissionais.</p></div><div className="messages-admin-secure"><ShieldCheck/><span><strong>Envio controlado</strong><small>Confirme canal e audiência antes de enviar.</small></span></div></div>

      {/* Stats */}
      <div className="messages-admin-context grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500/10 rounded-lg"><Mail className="w-5 h-5 text-blue-400" /></div>
            <div>
              <p className="text-sm font-bold text-white capitalize">{notification.type === 'push' ? 'Notificação push' : notification.type}</p>
              <p className="text-gray-400 text-xs">Canal selecionado</p>
            </div>
          </div>
        </div>
        <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-500/10 rounded-lg"><Smartphone className="w-5 h-5 text-green-400" /></div>
            <div>
              <p className="text-sm font-bold text-white">{notification.target === 'all' ? 'Todos' : notification.target === 'workers' ? 'Profissionais' : notification.target === 'clients' ? 'Clientes' : 'Individual'}</p>
              <p className="text-gray-400 text-xs">Audiência selecionada</p>
            </div>
          </div>
        </div>
        <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-500/10 rounded-lg"><Bell className="w-5 h-5 text-purple-400" /></div>
            <div>
              <p className="text-sm font-bold text-white">{notification.message.length} caracteres</p>
              <p className="text-gray-400 text-xs">Tamanho da mensagem</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="messages-admin-tabs border-b border-gray-700">
        <div className="flex gap-1">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors relative
                ${activeTab === tab.id
                  ? 'text-teal-400 border-b-2 border-teal-400'
                  : 'text-gray-400 hover:text-white hover:bg-gray-800'}`}
            >
              <tab.icon className="w-4 h-4 inline mr-2" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Compose */}
      {activeTab === 'compose' && (
        <div className="messages-admin-compose bg-gray-800 rounded-xl border border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-white mb-1">Nova notificação</h3><p className="text-gray-500 text-xs mb-5">Defina o canal, a audiência e reveja o conteúdo antes do envio.</p>
          <form onSubmit={handleSend} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-400 text-sm mb-1">Tipo de Notificação</label>
                <div className="flex gap-2">
                  {[
                    { value: 'email', label: 'Email', icon: Mail },
                    { value: 'sms', label: 'SMS', icon: Smartphone },
                    { value: 'push', label: 'Push', icon: Bell },
                  ].map(type => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setNotification({ ...notification, type: type.value })}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all flex-1
                        ${notification.type === type.value
                          ? 'border-teal-500 bg-teal-500/10 text-teal-400'
                          : 'border-gray-700 text-gray-400 hover:border-gray-600'}`}
                    >
                      <type.icon className="w-4 h-4" />
                      <span className="text-sm">{type.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* O que esta mensagem é — e não é uma formalidade.
                  Uma campanha respeita quem desligou promoções; um aviso de
                  funcionamento não pode ser desligado, porque quem não o
                  receber perde trabalho. Marcar publicidade como aviso da
                  plataforma é entrar à força numa caixa que disse que não. */}
              <div>
                <label className="block text-gray-400 text-sm mb-1">Tipo de mensagem</label>
                <div className="flex gap-2">
                  {[
                    { value: 'novidades', label: 'Novidade ou promoção', nota: 'Quem desligou promoções não recebe' },
                    { value: 'plataforma', label: 'Aviso da plataforma', nota: 'Manutenção e indisponibilidade · não se desliga' },
                  ].map((c) => (
                    <button key={c.value} type="button"
                      onClick={() => setNotification({ ...notification, categoria: c.value })}
                      className={`flex-1 text-left px-4 py-2 rounded-lg border transition-all
                        ${notification.categoria === c.value
                          ? 'border-teal-500 bg-teal-500/10 text-teal-300'
                          : 'border-gray-700 text-gray-400 hover:border-gray-600'}`}>
                      <span className="block text-sm font-medium">{c.label}</span>
                      <span className="block text-xs opacity-70 mt-0.5">{c.nota}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-gray-400 text-sm mb-1">Destinatário</label>
                <div className="flex gap-2">
                  {[
                    { value: 'all', label: 'Todos', icon: Users },
                    { value: 'workers', label: 'Profissionais', icon: Briefcase },
                    { value: 'clients', label: 'Clientes', icon: User },
                    { value: 'single', label: 'Individual', icon: User },
                  ].map(target => (
                    <button
                      key={target.value}
                      type="button"
                      onClick={() => setNotification({ ...notification, target: target.value })}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all flex-1
                        ${notification.target === target.value
                          ? 'border-teal-500 bg-teal-500/10 text-teal-400'
                          : 'border-gray-700 text-gray-400 hover:border-gray-600'}`}
                    >
                      <target.icon className="w-4 h-4" />
                      <span className="text-sm">{target.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {notification.target === 'single' && (
              <div>
                <label className="block text-gray-400 text-sm mb-1">ID do Destinatário</label>
                <input
                  type="text"
                  value={notification.recipientId}
                  onChange={(e) => setNotification({ ...notification, recipientId: e.target.value })}
                  placeholder="Digite o ID do utilizador"
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white 
                             placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
            )}

            <div>
              <label className="block text-gray-400 text-sm mb-1">Assunto</label>
              <input
                type="text"
                value={notification.subject}
                onChange={(e) => setNotification({ ...notification, subject: e.target.value })}
                placeholder="Assunto da notificação"
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white 
                           placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>

            <div>
              <label className="block text-gray-400 text-sm mb-1">Mensagem</label>
              <textarea
                value={notification.message}
                onChange={(e) => setNotification({ ...notification, message: e.target.value })}
                rows={6}
                placeholder="Digite a mensagem..."
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white 
                           placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
              />
            </div>

            <div className="flex items-center justify-between pt-2">
              <p className="text-gray-500 text-xs">
                {notification.type === 'sms' && 'Limite: 160 caracteres por SMS'}
                {notification.type === 'push' && 'Limite: 240 caracteres'}
                {notification.type === 'email' && 'Suporta HTML básico'}
              </p>
              <button
                type="submit"
                disabled={sending || !notification.subject || !notification.message}
                className="flex items-center gap-2 px-6 py-2 bg-teal-500 hover:bg-teal-600 text-white rounded-lg 
                           font-medium transition-colors shadow-lg shadow-teal-500/20 
                           disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send className="w-4 h-4" />
                {sending ? 'Enviando...' : 'Enviar Notificação'}
              </button>
            </div>

            {porConfirmar && (
              <div className="p-4 rounded-xl border border-amber-500/40 bg-amber-500/10">
                <p className="text-amber-200 text-sm font-semibold">
                  {porConfirmar.message}
                </p>
                <p className="text-amber-200/70 text-xs mt-1">
                  Sai para fora da Dozy e não se desfaz.{' '}
                  {notification.categoria === 'plataforma'
                    ? 'Como aviso da plataforma, chega a toda a gente — ninguém o pode ter desligado.'
                    : 'Quem tiver desligado “Novidades e promoções” nas preferências não recebe.'}
                </p>
                <div className="flex gap-2 mt-3">
                  <button type="button" onClick={() => setPorConfirmar(null)} disabled={sending}
                    className="px-4 min-h-[44px] rounded-lg bg-slate-800 text-slate-300">
                    Cancelar
                  </button>
                  <button type="button" onClick={(e) => handleSend(e, true)} disabled={sending}
                    className="px-4 min-h-[44px] rounded-lg bg-amber-600 text-white font-semibold disabled:opacity-50">
                    {sending ? 'A enviar…' : `Enviar mesmo assim a ${porConfirmar.recipients}`}
                  </button>
                </div>
              </div>
            )}
          </form>
        </div>
      )}

      {/* History */}
      {activeTab === 'history' && (
        <DataTable
          data={sentHistory}
          columns={historyColumns}
          pageSize={10}
          emptyMessage="Ainda não há histórico de envios. O registo de campanhas será apresentado aqui quando estiver disponível."
        />
      )}

      {/* Templates */}
      {activeTab === 'templates' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map(template => (
            <div key={template.id} className="message-template-card bg-gray-800 rounded-xl border border-gray-700 p-5 hover:border-gray-600 transition-colors">
              <div className="flex items-start justify-between mb-3">
                <div className="p-2 bg-gray-700 rounded-lg">
                  {template.type === 'email' && <Mail className="w-5 h-5 text-blue-400" />}
                  {template.type === 'sms' && <Smartphone className="w-5 h-5 text-green-400" />}
                  {template.type === 'push' && <Bell className="w-5 h-5 text-purple-400" />}
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium
                  ${template.type === 'email' ? 'bg-blue-500/20 text-blue-400' :
                    template.type === 'sms' ? 'bg-green-500/20 text-green-400' :
                    'bg-purple-500/20 text-purple-400'}`}>
                  {template.type.toUpperCase()}
                </span>
              </div>
              <h4 className="text-white font-medium mb-1">{template.name}</h4>
              <p className="text-gray-400 text-sm mb-4">{template.description}</p>
              <div className="flex gap-2">
                <button onClick={() => { setNotification({ ...notification, type: template.type, subject: template.name }); setActiveTab('compose'); }} className="flex-1 px-3 py-1.5 bg-teal-500/10 text-teal-400 rounded-lg text-sm hover:bg-teal-500/20 transition-colors font-medium">
                  Usar
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'ensaio' && (
        <div className="max-w-2xl">
          <div className="p-4 rounded-xl border border-slate-700 bg-slate-900/50">
            <h3 className="text-white font-semibold">Provar um canal</h3>
            <p className="text-sm text-gray-400 mt-1">
              Envia uma mensagem de exemplo para o destino que indicar, pelo canal
              real. Não cria notificações a ninguém nem altera contas. Fica no
              registo quem pediu e para onde.
            </p>

            <form onSubmit={enviarEnsaio} className="mt-4 space-y-3">
              <div className="flex gap-2">
                {['sms', 'email'].map((c) => (
                  <button key={c} type="button"
                    onClick={() => setEnsaio({ ...ensaio, canal: c, destino: '' })}
                    className={`px-4 min-h-[44px] rounded-lg text-sm font-semibold ${
                      ensaio.canal === c ? 'bg-teal-500/20 text-teal-300' : 'bg-slate-800 text-gray-400'}`}>
                    {c.toUpperCase()}
                  </button>
                ))}
              </div>

              <label className="block">
                <span className="text-xs text-gray-400">
                  {ensaio.canal === 'sms' ? 'Número (com indicativo)' : 'Email de destino'}
                </span>
                <input
                  value={ensaio.destino}
                  onChange={(e) => setEnsaio({ ...ensaio, destino: e.target.value })}
                  placeholder={ensaio.canal === 'sms' ? '+244 9XX XXX XXX' : 'nome@exemplo.ao'}
                  className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 p-3 text-sm text-slate-100"
                />
              </label>

              <label className="block">
                <span className="text-xs text-gray-400">Texto (opcional)</span>
                <textarea rows={3}
                  value={ensaio.texto}
                  onChange={(e) => setEnsaio({ ...ensaio, texto: e.target.value })}
                  placeholder="Deixe vazio para usar o texto de exemplo."
                  className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 p-3 text-sm text-slate-100"
                />
              </label>

              <button type="submit" disabled={ensaioBusy || !ensaio.destino}
                className="px-4 min-h-[44px] rounded-lg bg-teal-600 text-white font-semibold disabled:opacity-50">
                {ensaioBusy ? 'A enviar…' : 'Enviar ensaio'}
              </button>
            </form>

            {/* A resposta do fornecedor aparece tal como veio. É aí que está o
                motivo quando falha — "remetente não configurado", "número
                inválido" — e sem isso o ensaio só diria "não chegou". */}
            {ensaioResposta && (
              <pre className={`mt-4 p-3 rounded-lg text-xs overflow-x-auto ${
                ensaioResposta.success ? 'bg-green-500/10 text-green-300' : 'bg-red-500/10 text-red-300'}`}>
{JSON.stringify(ensaioResposta, null, 2)}
              </pre>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MessagesPage;
